import React, { useCallback, useEffect, useMemo, useState } from "react";

import type { ClientId } from "../../domain/types";
import { TALLY_CONNECTION_KEY, type TallyConnectionSummary } from "../../domain/constants";
import {
  fetchTallyStatus,
  retryTallyFetch,
  type TallyConnectStatus,
} from "../../data/tallyConnectApi";
import { persistAnswer, updateClient, useCoAnswer } from "../../data/store";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

const POLL_MS = 2000;

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="shrink-0 rounded-md border px-2.5 py-1.5 text-xs font-medium text-foreground"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function jobLine(status: string): string {
  if (status === "done") return "Read";
  if (status === "failed") return "Failed";
  if (status === "leased" || status === "pending") return "Reading";
  return "Waiting";
}

/**
 * The CA downloads the connector, signs in with the master admin account, and
 * picks this client. Continue stays closed until this company is loaded in
 * Tally and every read has finished.
 */
export function ConnectTallyStep({ clientId }: { clientId: ClientId }) {
  const { goNext } = useSubStepNav(clientId, "connect-tally");
  const recorded = useCoAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY);
  const [status, setStatus] = useState<TallyConnectStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const next = await fetchTallyStatus(clientId);
    setStatus(next);
    updateClient(clientId, {
      ...(next.company_name ? { tallyCompanyName: next.company_name } : {}),
      tallyGstin: next.gstin ?? "",
      tallyGstState: next.gst_state ?? "",
      tallyGstRegistrationType: next.gst_registration_type ?? "",
    });
    return next;
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    refresh().catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Could not start the Tally connection.");
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, refresh]);

  const watching = status !== null && !status.ready;

  useEffect(() => {
    if (!watching) return;
    const timer = window.setInterval(() => {
      refresh().catch((err) => setError(err instanceof Error ? err.message : "Could not refresh the read."));
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh, watching]);

  // The preview sits on every screen and cannot see this poll. Company name and
  // GSTIN also land on the client; the bank count only exists here, so it is
  // written once the bank read finishes rather than on every poll.
  const summary = useMemo<TallyConnectionSummary | null>(() => {
    if (!status?.company_matched || !status.company_name) return null;
    const banksRead = status.jobs.some((job) => job.id === "banks" && job.status === "done");
    const base: TallyConnectionSummary = {
      companyName: status.company_name,
      gstin: status.gstin,
    };
    if (!banksRead) return base;
    return { ...base, bankAccountCount: status.counts.banks };
  }, [status]);

  useEffect(() => {
    if (!summary) return;
    const bankAccountCount = summary.bankAccountCount ?? recorded?.bankAccountCount;
    const next =
      bankAccountCount === undefined ? summary : { ...summary, bankAccountCount };
    const same =
      recorded?.companyName === next.companyName &&
      (recorded?.gstin ?? null) === (next.gstin ?? null) &&
      (recorded?.bankAccountCount ?? null) === (next.bankAccountCount ?? null);
    if (same) return;
    void persistAnswer<TallyConnectionSummary>(clientId, TALLY_CONNECTION_KEY, next);
  }, [clientId, recorded, summary]);

  async function retry() {
    setBusy(true);
    setError(null);
    try {
      const next = await retryTallyFetch(clientId);
      setStatus(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not read the books again.");
    } finally {
      setBusy(false);
    }
  }

  const failed = status?.jobs.some((job) => job.status === "failed") ?? false;
  const companyFailure = status?.jobs.find((job) => job.id === "company" && job.status === "failed")?.error ?? null;
  const installer = status?.installer_url;
  const creating = status?.create_company === true;
  const companyRead = status?.jobs.find((job) => job.id === "company");
  const companyReadFinished = companyRead?.status === "failed" || companyRead?.status === "done";
  const awaitingOpen = creating && status?.company_created === true && !status.company_matched && companyReadFinished;
  const expectedName = status?.expected_company_name;
  const description = creating && !status?.company_matched
    ? "Download the connector, sign in with your master admin email and password, and pick this client. Leave TallyPrime open. Zournal will create this company in Tally."
    : "Download the connector, sign in with your master admin email and password, and pick this client. Load this company in Tally and leave it loaded. Another company may stay on screen. Do not shut this one.";

  return (
    <SubStepShell
      title="Connect Zournal to Tally"
      description={description}
      thenWhat="Next you will verify what was read from Tally."
      onContinue={() => void goNext()}
      continueDisabled={!status?.ready}
    >
      {installer && (
        <a
          href={installer}
          className="inline-flex w-fit rounded-lg border px-4 py-2 text-[13px] font-medium text-foreground"
        >
          Download the Tally connector
        </a>
      )}

      {!status?.enrolled && (
        <div className="rounded-lg border">
          <div className="flex items-center gap-3 px-5 py-4">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-muted-foreground">Zournal address</p>
              <p className="mt-1 break-all font-mono text-[13px] text-foreground">{window.location.origin}</p>
            </div>
            <CopyButton value={window.location.origin} label="Zournal address" />
          </div>
          <div className="border-t px-5 py-4 text-[13px] text-muted-foreground">
            In the connector, sign in with the same email and password you use here, then choose this client.
          </div>
        </div>
      )}

      {status?.enrolled && status.company_mismatch && (
        <div className="rounded-lg border border-destructive/40 px-5 py-4 text-[13px]">
          <p className="text-destructive">
            {awaitingOpen ? (
              <>
                <span className="font-medium">{expectedName}</span> was created in Tally. Open it and leave it
                loaded. Another company may stay on screen. Do not shut this one.
              </>
            ) : (
              <>
                Load <span className="font-medium">{status.expected_company_name}</span> in Tally and leave it
                loaded. Another company may stay on screen. Do not shut this one.
              </>
            )}
            {status.company_name ? (
              <>
                {" "}
                Loaded now: <span className="font-medium">{status.company_name}</span>.
              </>
            ) : null}
          </p>
          <button
            type="button"
            onClick={() => void retry()}
            disabled={busy}
            className="mt-3 rounded-lg border px-4 py-2 text-[13px] font-medium text-foreground"
          >
            Try again
          </button>
        </div>
      )}

      {status?.enrolled && !status.company_mismatch && (
        <div className="rounded-lg border px-5 py-4 text-[13px]">
          <p>
            {status.company_matched && status.company_name ? (
              <>
                <span className="font-medium text-foreground">{status.company_name}</span> is loaded in Tally.
              </>
            ) : status.create_error ? (
              <span className="text-destructive">{status.create_error}</span>
            ) : awaitingOpen ? (
              <>
                <span className="font-medium text-foreground">{expectedName}</span> was created in Tally. Open it
                and leave it loaded. Another company may stay on screen. Do not shut this one.
              </>
            ) : creating ? (
              <span className="text-muted-foreground">
                Creating {expectedName ?? "this company"} in Tally. Leave TallyPrime open.
              </span>
            ) : companyFailure ? (
              <span className="text-destructive">{companyFailure}</span>
            ) : (
              <span className="text-muted-foreground">Checking that this company is loaded in Tally…</span>
            )}
          </p>
          {(companyFailure || status.create_error || awaitingOpen) && (
            <button
              type="button"
              onClick={() => void retry()}
              disabled={busy}
              className="mt-2 block text-[13px] font-medium text-foreground underline-offset-2 hover:underline"
            >
              Try again
            </button>
          )}
        </div>
      )}

      {status?.enrolled && status.company_matched && (
        <ul className="flex flex-col gap-2">
          {status.jobs.filter((job) => job.id !== "company").map((job) => (
            <li key={job.id} className="flex flex-col gap-0.5 text-[13px]">
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-foreground">{job.label}</span>
                <span className={job.status === "failed" ? "text-destructive" : "text-muted-foreground"}>
                  {jobLine(job.status)}
                  {job.id === "ledgers" && job.status === "done" ? ` · ${status.counts.gl} accounts` : ""}
                  {job.id === "customers" && job.status === "done" ? ` · ${status.counts.customers}` : ""}
                  {job.id === "vendors" && job.status === "done" ? ` · ${status.counts.vendors}` : ""}
                  {job.id === "banks" && job.status === "done" ? ` · ${status.counts.banks}` : ""}
                  {job.id === "currencies" && job.status === "done" ? ` · ${status.counts.currencies}` : ""}
                </span>
              </div>
              {job.status === "failed" && job.error ? (
                <p className="text-right text-[12px] text-destructive">{job.error}</p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {failed && status?.company_matched && (
        <button
          type="button"
          onClick={() => void retry()}
          disabled={busy}
          className="w-fit text-[13px] font-medium text-foreground underline-offset-2 hover:underline"
        >
          Read the books again
        </button>
      )}

      {error && <p className="text-[13px] text-destructive">{error}</p>}
    </SubStepShell>
  );
}
